import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Admin, Session } from '@cockpit/api-contract'
import { logout as apiLogout } from '@/lib/api/auth'
import { configureApiClient } from '@/lib/apiClient'

interface AuthState {
  token: string | null
  admin: Admin | null
  /** Store a fresh session after a successful login. */
  login: (session: Session) => void
  /** User-initiated sign out: fire-and-forget DELETE /session, then clear. */
  logout: () => void
  /** Clear local auth state without hitting the API (used on a 401). */
  clear: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      admin: null,
      login: (session) => set({ token: session.token, admin: session.admin }),
      logout: () => {
        void apiLogout().catch(() => {})
        set({ token: null, admin: null })
      },
      clear: () => set({ token: null, admin: null }),
    }),
    {
      name: 'cockpit.auth',
      partialize: (s) => ({ token: s.token, admin: s.admin }),
    },
  ),
)

export const selectIsAuthenticated = (s: AuthState): boolean => s.token !== null
export const selectIsSuperAdmin = (s: AuthState): boolean => s.admin?.role === 'super_admin'

// Wire the API client to auth state. On a 401 we clear locally (no API call —
// the token is already dead) and hard-redirect to the login screen.
configureApiClient({
  getToken: () => useAuthStore.getState().token,
  onUnauthorized: () => {
    useAuthStore.getState().clear()
    window.location.assign('/login')
  },
})
